import { useIsMounted } from '@common/hooks';
import { isUndefined } from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';
import { PaginationComponentProps } from 'react-data-table-component';
import ReactPaginate from 'react-paginate';

export interface IDataTablePaginationProps extends PaginationComponentProps {
  pageRangeDisplayed?: number;
  paginationLayout?: 'inline-right' | 'stacked-vertical';
}

export const DataTablePagination = observer(
  ({
    rowsPerPage,
    rowCount,
    currentPage,
    onChangePage,
    onChangeRowsPerPage,
    pageRangeDisplayed = 5,
    paginationLayout = 'inline-right',
  }: IDataTablePaginationProps) => {
    const isMounted = useIsMounted();

    const pageNumbers = useMemo(() => {
      return Math.ceil(rowCount / rowsPerPage);
    }, [rowCount, rowsPerPage]);

    const lastIndex = currentPage * rowsPerPage;
    const firstIndex = lastIndex - rowsPerPage + 1;

    const handlePageChange = useCallback(
      (pageNumber: number) => {
        if (!isUndefined(pageNumber) && isMounted()) {
          onChangePage(pageNumber + 1, rowCount);
        }
      },
      [rowCount, onChangePage, isMounted],
    );

    const handleRowsPerPage = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        onChangeRowsPerPage(Number(e.target.value), currentPage);
      },
      [currentPage, onChangeRowsPerPage],
    );

    // TODO: maybe allow this to be set from RemoteDataTable.tsx in the future?
    const [defaultRowsPerPageOptions] = useState([10, 25, 50, 100]);

    const range =
      currentPage === pageNumbers
        ? `${firstIndex}-${rowCount} of ${rowCount}`
        : `${firstIndex}-${lastIndex} of ${rowCount}`;

    const isStackedVertical = paginationLayout === 'stacked-vertical';

    const RowsPerPageBlock = (
      <>
        <span className="me-2">Rows per page:</span>
        <select
          className="form-select form-select-sm me-3"
          style={{ width: '80px' }}
          onChange={handleRowsPerPage}
          value={rowsPerPage}
          data-testid="rowsPerPage"
        >
          {defaultRowsPerPageOptions.map((num: number) => (
            <option key={num} value={num} data-testid="rpp-option">
              {num}
            </option>
          ))}
        </select>
        <span className="ms-2">{range}</span>
      </>
    );

    const Pager = (
      <ReactPaginate
        pageCount={pageNumbers}
        pageRangeDisplayed={pageRangeDisplayed}
        marginPagesDisplayed={1}
        onPageChange={(args) => handlePageChange(args?.selected)}
        initialPage={Math.max(0, currentPage - 1)}
        disableInitialCallback={true}
        breakClassName={'page-item'}
        breakLinkClassName={'page-link'}
        containerClassName={'pagination mb-0'}
        pageClassName={'page-item'}
        pageLinkClassName={'page-link'}
        previousClassName={'page-item'}
        previousLinkClassName={'page-link'}
        nextClassName={'page-item'}
        nextLinkClassName={'page-link'}
        activeClassName={'active'}
      />
    );

    return (
      <>
        {isStackedVertical ? (
          <>
            <hr className="mt-3 mb-2" />
            <nav
              className="pagination mt-2 px-1 d-flex justify-content-center align-items-center"
              data-testid="datapager"
            >
              {RowsPerPageBlock}
            </nav>
            <div className="d-flex justify-content-center mt-3">{Pager}</div>
          </>
        ) : (
          <nav
            className="pagination mt-3 px-1 d-flex justify-content-end align-items-center flex-nowrap"
            data-testid="datapager"
          >
            {RowsPerPageBlock}
            <div className="ms-3 d-flex align-items-center">{Pager}</div>
          </nav>
        )}
      </>
    );
  },
);
